import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';

// Eager load critical pages or layouts
import MainLayout from './components/MainLayout';
import NeuralBackground from "./components/NeuralBackground";

// Lazy load pages to reduce bundle size
const Pricing = lazy(() => import("./pages/Pricing"));
const Homepage = lazy(() => import('./pages/Homepage'));
const Login = lazy(() => import('./pages/Login'));
const Dashboard = lazy(() => import('./components/Dashboard'));
const PrivateRoute = lazy(() => import('./components/PrivateRoute')); // Keep components eager if small

const AutomationLanding = lazy(() => import('./pages/AutomationLanding'));
const ConsultationLandingV2 = lazy(() => import('./pages/ConsultationLandingV2'));
const AILanding = lazy(() => import('./pages/AILanding'));
const SaaSLanding = lazy(() => import('./pages/SaaSLanding'));

const AboutUs = lazy(() => import("./components/Aboutus"));

const Downloads = lazy(() => import('./pages/Downloads'));
const Blog = lazy(() => import('./pages/Blog'));
const Videos = lazy(() => import('./pages/Videos'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const Careers = lazy(() => import('./pages/Careers'));
const JobDetail = lazy(() => import('./pages/JobDetail'));
const FAQs = lazy(() => import('./components/FAQs.jsx'));

// Services
const AutomationServices = lazy(() => import("./pages/automation/AutomationServices"));
const WorkflowAutomation = lazy(() => import("./pages/automation/WorkflowAutomation.jsx"));
const ProcessAutomation = lazy(() => import("./pages/automation/ProcessAutomation.jsx"));
const BusinessAutomation = lazy(() => import("./pages/automation/BusinessAutomation.jsx"));

const AutomationLandingDispatcher = lazy(() => import("./pages/AutomationLandingDispatcher"));
const AutomationLandingB = lazy(() => import("./pages/AutomationLandingB"));
const AutomationLandingE = lazy(() => import("./pages/AutomationLandingE"));

// Admin
const AdminLogin = lazy(() => import('./pages/admin/AdminLogin.jsx'));
const AdminDashboard = lazy(() => import('./pages/admin/AdminDashboard.jsx'));
const AdminBlogList = lazy(() => import('./pages/admin/AdminBlogList.jsx'));
const AdminAddBlog = lazy(() => import('./pages/admin/AdminAddBlog.jsx'));
const BlogNew = lazy(() => import('./pages/Blogsnew.jsx'));
const BlogPostNew = lazy(() => import('./pages/BlogPostNew.jsx'));
const AdminManageBlogs = lazy(() => import('./pages/admin/AdminManageBlogs.jsx'));
const AdminEditBlog = lazy(() => import('./pages/admin/AdminEditBlog.jsx'));
const AdminBlogPreview = lazy(() => import('./pages/admin/AdminBlogPreview.jsx'));
const AdminManageJobs = lazy(() => import('./pages/admin/AdminManageJobs.jsx'));
const AdminAddJob = lazy(() => import('./pages/admin/AdminAddJob.jsx'));
const AdminEditJob = lazy(() => import('./pages/admin/AdminEditJob.jsx'));

function App() {
  return (
    <HelmetProvider>
      <Router>
        <Suspense fallback={
          <div className="relative min-h-screen text-white flex items-center justify-center">
            <NeuralBackground />
            <div className="text-center z-10">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1E90FF] mx-auto mb-4"></div>
              <p className="text-gray-400">Loading...</p>
            </div>
          </div>
        }>
          <Routes>

            {/* RESOURCES */}
            <Route path="/resources/downloads" element={<Downloads />} />
            <Route path="/resources/blog" element={<Blog />} />
            <Route path="/resources/videos" element={<Videos />} />
            <Route path="/blog/:slug" element={<BlogPost />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            <Route path="/admin/blogs/list" element={<AdminBlogList />} />
            <Route path="/admin/blogs/add" element={<AdminAddBlog />} />
            <Route path="/blogs-new" element={<BlogNew />} />
            <Route path="/blog-new/:slug" element={<BlogPostNew />} />
            <Route path="/admin/blogs/manage" element={<AdminManageBlogs />} />
            <Route path="/admin/blogs/edit/:id" element={<AdminEditBlog />} />
            <Route path="/admin/blogs/preview/:id" element={<AdminBlogPreview />} />
            <Route path="/admin/jobs/manage" element={<AdminManageJobs />} />
            <Route path="/admin/jobs/add" element={<AdminAddJob />} />
            <Route path="/admin/jobs/edit/:id" element={<AdminEditJob />} />
            {/* PUBLIC PAGES WRAPPED IN MAINLAYOUT */}
            <Route path="/" element={<MainLayout><Homepage /></MainLayout>} />

            <Route path="/aboutus" element={<MainLayout><AboutUs /></MainLayout>} />

            <Route path="/pricing" element={<MainLayout><Pricing /></MainLayout>} />

            <Route path="/careers" element={<MainLayout><Careers /></MainLayout>} />
            <Route path="/careers/:jobId" element={<MainLayout><JobDetail /></MainLayout>} />
            <Route path="/faqs" element={<MainLayout><FAQs /></MainLayout>} />

            {/* MAIN SERVICES */}
            <Route path="/automation" element={<MainLayout><AutomationLanding /></MainLayout>} />
            <Route path="/consultation" element={<MainLayout><ConsultationLandingV2 /></MainLayout>} />
            <Route path="/saas" element={<MainLayout><SaaSLanding /></MainLayout>} />
            <Route path="/ai" element={<MainLayout><AILanding /></MainLayout>} />

            {/* NEW AUTOMATION SERVICE PAGES */}
            <Route
              path="/workflow-automation"
              element={<MainLayout><WorkflowAutomation /></MainLayout>}
            />
            <Route path="/automation-services" element={<MainLayout><AutomationServices /></MainLayout>} />
            {/* AUTOMATION LANDING VARIANTS */}
            <Route
              path="/automation-flow"
              element={<MainLayout><AutomationLandingDispatcher /></MainLayout>}
            />

            <Route
              path="/automation-b"
              element={<MainLayout><AutomationLandingB /></MainLayout>}
            />

            <Route
              path="/automation-e"
              element={<MainLayout><AutomationLandingE /></MainLayout>}
            />        <Route path="/business-automation" element={<MainLayout><BusinessAutomation /></MainLayout>} />
            <Route path="/process-automation" element={<MainLayout><ProcessAutomation /></MainLayout>} />

            {/* LOGIN SECTION */}
            <Route path="/login" element={<Login />} />

            {/* PROTECTED ROUTES */}
            <Route path="/dashboard" element={
              <PrivateRoute><Dashboard /></PrivateRoute>
            } />
            <Route path="*" element={<Navigate to="/" replace />} />

          </Routes>
        </Suspense>
      </Router>
    </HelmetProvider>
  );
}

export default App;
